import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Resonor (Act II, ACT2-BIBLE.md §7) — the burst weapon.
 *
 * Where the other single-target weapons pay out one bolt per interval, the
 * Resonor banks the whole interval and spends it at once: a lead shot at full
 * weight followed by the rest of the volley on a tight subdivision. Front-
 * loading the damage is the character — it lands hard on arrival and then
 * trickles, so it rewards firing into something that is already committed.
 *
 * The burst's shots are queued as seconds-remaining entries rather than fired
 * on one frame, so each aims from wherever the player actually is when it
 * leaves — the same pattern Nova Pulse's echoes use.
 */
export class Resonor extends Weapon {
  constructor() {
    super(WEAPONS.resonor);
    this._pending = []; // { timer, mult }
  }

  /** Shots per volley: 2 at level 1, one more at each of levels 3, 5, 7. */
  get burstCount() {
    return this.config.burst + (this.level >= 3 ? 1 : 0) + (this.level >= 5 ? 1 : 0) + (this.level >= 7 ? 1 : 0);
  }

  _shotDamage() {
    return this.config.damage + this.level * 1.3;
  }

  update(dt, origin, target, pool) {
    this.fireFlash = Math.max(0, this.fireFlash - dt);
    for (let i = this._pending.length - 1; i >= 0; i--) {
      this._pending[i].timer -= dt;
      if (this._pending[i].timer <= 0) {
        const { mult } = this._pending[i];
        this._pending.splice(i, 1);
        this._fireShot(origin, target, pool, mult);
      }
    }

    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = this.config.fireRate;
    this.fire(origin, target, pool);
    this.fireFlash = 0.12;
  }

  fire(origin, target, pool) {
    // The lead shot leaves immediately at full weight; the rest of the burst
    // is queued behind it on the subdivision.
    this._fireShot(origin, target, pool, this.config.leadShotMultiplier);
    for (let i = 1; i < this.burstCount; i++) {
      this._pending.push({ timer: this.config.subdivision * i, mult: 1 });
    }
  }

  _fireShot(origin, target, pool, damageMultiplier) {
    if (!target) return;
    const angle = Math.atan2(target.y - origin.y, target.x - origin.x);
    pool.fire({
      x: origin.x,
      y: origin.y,
      vx: Math.cos(angle) * this.config.projectileSpeed,
      vy: Math.sin(angle) * this.config.projectileSpeed,
      radius: this.config.projectileRadius,
      damage: this._shotDamage() * damageMultiplier,
      hits: 1,
      lifetime: this.config.lifetime,
      color: this.config.color,
    });
  }
}
